import { isWeb3Mode } from './authMode'

export const isSelfOrderEnabled = (): boolean =>
  import.meta.env.VITE_SELF_ORDER_ENABLED === 'true'

// #284 出前機能（店舗自前配達）. Gated by env var so we can ship the code
// but keep the entire flow invisible in prod until piloted. Turning this on
// also requires at least one store to have `deliveryEnabled: true`.
// #2549 本番は OFF に戻した (#864 で一度 ON にしたが、まだ本番に出さない)。
export const isDeliveryEnabled = (): boolean =>
  import.meta.env.VITE_DELIVERY_ENABLED === 'true'

// #1619 タクシーの配車ハブ (/ride)。Uber / GO への外部リンクと、協業後のアプリ内配車の準備中の案内。
// #2549 まだ本番に出さない: ホームのカードと /ride の両方を止める。demo・preview・手元は ON。
export const isRideEnabled = (): boolean =>
  import.meta.env.VITE_RIDE_ENABLED === 'true'

// #1154 AR宝探しラリー。WebAR アプリの URL（同一ドメインの /ar/ パス配信を想定、
// 相対パス可）。未設定 = AR 導線を一切表示しないデプロイレベルの安全弁。
// 本体の開催 ON/OFF はデータ駆動 (type='ar' イベントの isEnabled/期間)。
export const arWebArUrl = (): string =>
  import.meta.env.VITE_AR_WEBAR_URL || ''

// #1237 旅前プランナー。backend の PLAN_ENABLED と対の frontend gate —
// 未設定/false で Home 導線と /plan ルート一式が消える (二重ガード)。
export const isPlanEnabled = (): boolean =>
  import.meta.env.VITE_PLAN_ENABLED === 'true'

// #1248 体験チケット。backend の EXPERIENCE_ENABLED と対の frontend gate —
// 未設定/false で購入 CTA・/my-tickets・店舗消し込みルートが消える (二重ガード)。
export const isExperienceEnabled = (): boolean =>
  import.meta.env.VITE_EXPERIENCE_ENABLED === 'true'

// ---------------------------------------------------------------------------
// #1665 許可・資格待ちの機能 (license-pending features)
//
// **実装は完了しているが、法令上の許可・登録・資格が下りるまで本番運用しては
// いけない**機能のための共通 gate。融資・商談で「動くもの」を見せる必要がある
// 一方、prod の実顧客に使わせると無登録営業になる — その線を引くのがここ。
// 個別 flag を都度増やすと「どれが許可待ちか」が分からなくなるため 1 つに集約する。
//
// 既定は `VITE_DEMO_MODE` に追従 (demo/preview = ON、prod = OFF)。ピッチ当日に
// prod で見せたい等は `VITE_LICENSE_PENDING_DEMO` で明示上書きできる。
//
// ## 使い分け (混ぜない)
//
// - **恒久機能の kill switch** (`PLAN_ENABLED` / `EXPERIENCE_ENABLED` 等) …
//   本運用の ON/OFF。許可の話ではない
// - **単なる外部リンク・表示** … gate 不要。許可が要らないものを隠さない
//   (例: 東横INN 公式へのリンク #1665 — 公開サイトへの送客なので prod でも出す)
// - **ここ** … 許可・登録が下りるまで**動かしてはいけない**もの
//
// ## ⚠️ frontend gate だけでは不十分
//
// 無登録営業を防ぐのが目的なので、**backend 側も env gate で閉じる**こと
// (`EXPERIENCE_ENABLED` と同じ router 全体 404 の Depends 方式)。画面を隠すだけ
// では API が叩けてしまい、gate の意味がない。
//
// ## 収録中の機能 (増やしたらここに 1 行足す)
//
// | 機能 | issue | 解禁条件 |
// |---|---|---|
// | チャージバック保証 (有償オプション) | #1655 | 保険業法・保証業の該当性の法務確認 (#605) |
// | ↑ 予定: 民泊の予約・決済 (Phase 2) | #1666 | 住宅宿泊仲介業者の登録完了 |
// | ↑ 予定: 宿泊予約の代行 | #606 | 旅行業登録 (該当性の法務確認が先) |
//
// ## 解禁の作法
//
// 許可が下りたら **flag を true 固定にするのではなく `if` ごと削除**する
// (この表の行も消す)。true 固定で残すと、gate を OFF にしたときに解禁済みの
// 機能まで消える = この gate が二度と安全に切れなくなる。
export const isLicensePendingDemoEnabled = (): boolean => {
  const explicit = import.meta.env.VITE_LICENSE_PENDING_DEMO
  if (explicit === 'true') return true
  if (explicit === 'false') return false
  return import.meta.env.VITE_DEMO_MODE === 'true'
}

// #1190 Phase 4: YOKOSO 通貨 (ポイント) 支払を明示的な支払手段として出す gate。
//
// これまで JPYC balance 支払は Web3 mode 条件付きで hide されていた
// (QRScan.tsx L678 の `web3 && ...`)。Phase 4 では demo mode でも常時
// 表示する — 提案書 §3 の「YOKOSO 通貨で払うと 2%」を投資家/自治体向け
// デモで見せられるようにするため。prod 本番運用への露出は Phase 2/3 の
// 実運用安定後に別 issue で判断 (現状の非同期 top-up UX と噛み合わない)。
//
// VITE_YOKOSO_POINTS_ENABLED を明示的に指定できるが、未設定なら
// VITE_DEMO_MODE の値または Web3 mode に fallback する。
export const isYokosoPointsEnabled = (): boolean => {
  const explicit = import.meta.env.VITE_YOKOSO_POINTS_ENABLED
  if (explicit === 'true') return true
  if (explicit === 'false') return false
  return import.meta.env.VITE_DEMO_MODE === 'true' || isWeb3Mode()
}
